import React, { FC, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { IPageable, IQrUser, IRoute } from 'models';
import { Button, message, Upload } from 'antd';
import { EmptySearch, ErrorRequest, Header, Layout } from 'components/layout';
import qrStore from 'store/QrStore';
import strings from 'tools';
import QrCodesTable from 'components/qrcodes';
import QrCodesAddForm from 'components/qrcodes/QrCodesAdd/QrCodesAdd';
import './qrcodes.scss';
import { DownloadOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';

interface Props {
    navigation: IRoute[];
}

const QrCodes: FC<Props> = observer((props: Props) => {
    const { navigation } = props;
    const inputRef = useRef<HTMLInputElement>(null);
    const [isAddQrUser, setIsAddQrUser] = useState<boolean>(false);
    const [pageable, setPageable] = useState<IPageable>({ page: 0, size: 10 });
    const { qrUsers, isLoading } = qrStore;

    const handleUpload = async ({ file }: { file: File }) => {
        try {
            const responseData = await qrStore.uploadQrFile(file);
            const a = document.createElement('a');
            a.href = responseData.link;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            qrStore.getQrUsers(pageable);
        } catch (error) {
            message.error('Ошибка загрузки:', error);
        }
    };

    useEffect(() => {
        qrStore.getQrUsers(pageable);
    }, [pageable]);

    useEffect(() => {
        inputRef.current?.focus();
        setPageable({ page: 0, size: 10 });
    }, []);

    const addQrUserSubmit = async (data: IQrUser) => {
        await qrStore.addQrUser(data);
        qrStore.getQrUsers(pageable);
        setIsAddQrUser(false);
    };

    const downloadQrFile = (link: string) => {
        if (link) {
            window.open(link, '_blank');
        }
    };

    const downloadTemplate = () => {
        const link = document.createElement('a');
        link.href = '/Шаблон Excel.xlsx';
        link.download = 'Шаблон Excel.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const changePage = (pageNum: number) => {
        const newPageable = { ...pageable };
        newPageable.page = pageNum;
        setPageable(newPageable);
    };

    return (
        <Layout page="qrcodes" navigation={navigation} errorScreen>
            <Header
                title="QR-коды"
                description={
                    qrUsers
                        ? `Всего ${qrUsers.totalElements} ${strings.ending(qrUsers.totalElements, [
                              'QR-код',
                              'QR-кода',
                              'QR-кодов',
                          ])}`
                        : ''
                }
            />
            <div className="page-offers">
                <div className="offers-top">
                    <Upload
                        accept=".csv, .xls, .xlt, .xlm, .xlsx"
                        beforeUpload={(file) => {
                            handleUpload({ file });
                            return false;
                        }}
                        showUploadList={false}
                    >
                        <Button type="primary" icon={<UploadOutlined />}>
                            Загрузить файл для генерации QR
                        </Button>
                    </Upload>
                    <Button type="primary" icon={<DownloadOutlined />} onClick={downloadTemplate}>
                        Скачать шаблон Excel
                    </Button>
                    <Button
                        className="qr-button"
                        onClick={() => setIsAddQrUser(true)}
                        type="primary"
                        icon={<PlusOutlined />}
                    >
                        Создать QR-код
                    </Button>
                </div>
                {qrUsers?.totalElements && (qrUsers?.totalElements > 0 || isLoading) && (
                    <QrCodesTable
                        data={qrUsers?.content}
                        loading={isLoading}
                        page={pageable.page}
                        pageSize={pageable.size}
                        total={qrUsers?.totalElements}
                        onChangePage={changePage}
                        downloadQrFile={downloadQrFile}
                    />
                )}
                {!qrUsers?.totalElements && !isLoading && <EmptySearch />}
                {!qrUsers && <ErrorRequest onClick={() => qrStore.getQrUsers(pageable)} />}
            </div>
            <QrCodesAddForm
                open={isAddQrUser}
                onCancel={() => setIsAddQrUser(false)}
                onSubmit={addQrUserSubmit}
            />
        </Layout>
    );
});

export default QrCodes;
